/* global window */
import * as React from 'react';

type EventFn<T> = (event: T) => void;
interface EventFnMap {
  [eventName: string]: EventFn<any>[] | undefined;
  keydown?: EventFn<KeyboardEvent>[];
}

type UseHandleCapturedEventsRef = <T extends HTMLElement>(initialRefValue: T | null, eventFnMap: EventFnMap) => React.RefObject<T>;

export const targetCaptured = (element: HTMLElement, target: Node) => element.contains(target);

export const useHandleCapturedEventsRef: UseHandleCapturedEventsRef = (initialRefValue, eventFnMap) => {
  const eventWrapperRef = React.useRef(initialRefValue);

  const eventConfigs = Object.keys(eventFnMap)
    .map(eventFnMapKey => {
      const eventFnArray = eventFnMap[eventFnMapKey] || [];
      return eventFnArray.map(eventFn => {
        const downHandler = (event: Event) => {
          if (eventWrapperRef.current && targetCaptured(eventWrapperRef.current, event.target as Node)) {
            eventFn(event);
          }
        };
        return { eventName: eventFnMapKey, downHandler };
      });
    })
    .reduce((eventFnMapArrays, nextEventFnMapArray) => [...eventFnMapArrays, ...nextEventFnMapArray], []);

  React.useEffect(() => {
    eventConfigs.forEach(eventConfig => window.addEventListener(eventConfig.eventName, eventConfig.downHandler));
    return () => {
      eventConfigs.forEach(eventConfig => window.removeEventListener(eventConfig.eventName, eventConfig.downHandler));
    };
  });

  return eventWrapperRef;
};
